import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Search, AlertCircle, Info, AlertTriangle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { verifyHashChain } from "@/services/hashService";
import { toast } from "sonner";

export default function AuditLog() {
  const [searchTerm, setSearchTerm] = useState("");
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>("all");
  const [isVerifying, setIsVerifying] = useState(false);
  const [lastVerified, setLastVerified] = useState<Date | null>(null);

  const { data: logs, isLoading } = useQuery({
    queryKey: ["audit_logs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("audit_logs" as any)
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);
      
      if (error) throw error;
      return data;
    },
  });

  const filteredLogs = logs?.filter((log: any) => {
    const matchesSearch = 
      log.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.action.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSeverity = severityFilter === "all" || log.severity === severityFilter;
    const matchesEntityType = entityTypeFilter === "all" || log.entity_type === entityTypeFilter;

    return matchesSearch && matchesSeverity && matchesEntityType;
  });

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case "critical":
        return <AlertCircle className="h-4 w-4 text-destructive" />;
      case "warning":
        return <AlertTriangle className="h-4 w-4 text-warning" />;
      default:
        return <Info className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getSeverityBadge = (severity: string) => {
    const variants: Record<string, "default" | "destructive" | "secondary"> = {
      critical: "destructive",
      warning: "default",
      info: "secondary",
    };
    return variants[severity] || "secondary";
  };

  const handleVerifyIntegrity = async () => {
    setIsVerifying(true);
    try {
      const records = logs?.map((log: any) => ({
        record_hash: log.record_hash,
        previous_record_hash: log.previous_record_hash,
        created_at: log.created_at
      })) || [];

      const result = await verifyHashChain(records);
      
      if (result.valid) {
        setLastVerified(new Date());
        toast.success("Integrity Verified", {
          description: `All ${records.length} audit records verified successfully. Hash chain is intact.`
        });
      } else {
        toast.error("Integrity Breach Detected", {
          description: `Hash chain broken at record ${result.brokenAt}. Possible tampering detected.`
        });
      }
    } catch (error) {
      toast.error("Verification Failed", {
        description: error instanceof Error ? error.message : "Unable to verify audit trail integrity"
      });
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold">Audit Log</h1>
            <p className="text-muted-foreground">
              Complete audit trail with cryptographic hash-chain verification
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Button 
              onClick={handleVerifyIntegrity} 
              disabled={isVerifying || !logs?.length}
              variant="outline"
              className="gap-2"
            >
              <ShieldCheck className="w-4 h-4" />
              {isVerifying ? "Verifying..." : "Verify Integrity"}
            </Button>
            {lastVerified && (
              <p className="text-xs text-muted-foreground">
                Last verified: {format(lastVerified, "MMM d, yyyy 'at' h:mm a")}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="All Severities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Severities</SelectItem>
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>
          <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="deal">Deals</SelectItem>
              <SelectItem value="athlete">Athletes</SelectItem>
              <SelectItem value="user">Users</SelectItem>
              <SelectItem value="settings">Settings</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <div className="space-y-3">
            {filteredLogs?.map((log: any) => (
              <Card key={log.id}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      {getSeverityIcon(log.severity)}
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant={getSeverityBadge(log.severity)} className="capitalize">
                            {log.severity}
                          </Badge>
                          <Badge variant="outline" className="capitalize">
                            {log.entity_type}
                          </Badge>
                          <Badge variant="outline" className="capitalize">
                            {log.action.replace(/_/g, " ")}
                          </Badge>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {format(new Date(log.created_at), "MMM d, yyyy h:mm a")}
                        </span>
                      </div>
                      <p className="text-sm">{log.details}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>
                          <span className="font-medium">User:</span> {log.user_name || "System"}
                        </span>
                        {log.user_role && (
                          <span>
                            <span className="font-medium">Role:</span> {log.user_role.replace(/_/g, " ")}
                          </span>
                        )}
                      </div>
                      {log.metadata && Object.keys(log.metadata).length > 0 && (
                        <details className="text-xs">
                          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                            View metadata
                          </summary>
                          <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-x-auto">
                            {JSON.stringify(log.metadata, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {filteredLogs?.length === 0 && (
              <Card>
                <CardContent className="text-center py-8 text-muted-foreground">
                  No audit logs found matching your filters.
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
